import React, { useState, useEffect, useRef } from "react";
import { IOemSubscription, Person } from "../../models/person";
import { OEM, Supplier } from "../../models/supplier";
import { useAppDispatch, useAppSelector } from "../../reducers/store";
import { updateProfiles, updateFQMProfiles, getFQMProfiles, getProfiles as getProfilesAction } from "../../reducers/profileSlice";
import Select, { MultiValue, SingleValue } from "react-select";
import { getOEMs, selectSelectedSupplier, updateSupplierOem } from "../../reducers/supplierSlice";
import { Agreement, AgreementReturnModel } from "../../models/agreement";

interface NotificationSettings {
  name: string;
  description: string;
  settings: {
    name: string;
    options: {
      name: string;
      checked: () => boolean;
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    }[];
  }[];
}

export default function NotificationSettings(props: {
  selectedSupplier: Supplier;
  enableAllNotifications: boolean;
  setEnableAllNotifications: (value: boolean) => void;
  setSaveStatus: (value: boolean) => void;
}): JSX.Element {
  const dispatch = useAppDispatch();
  const [profiles, setProfiles] = useState<Person[]>([]);
  const [uniqueOems, setUniqueOems] = useState<OEM[]>([]);
  const [selectedOems, setSelectedOems] = useState<OEM[]>([]);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [initialToggle, setInitialToggle] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<Person>(
    null as any as Person,
  );
  const [saving, setSaving] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [opacityClass, setOpacityClass] = useState("opacity-0");
  const isUpdatingOems = useRef(false);

  const notificationSettings: NotificationSettings[] = [
    {
      name: "Quality Alerts",
      description:
        "Get information related to quality alerts and near misses written for this supplier.",
      settings: [
        {
          name: "Creation Notifications",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.qaCreation ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaCreation", event),
            },
            {
              name: "Text",
              checked: () => selectedProfile?.qaText ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaText", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.qaCreationPush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaCreationPush", event),
            }
          ],
        },
        {
          name: "Update Notifications",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.qaUpdate ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaUpdate", event),
            },
            {
              name: "Text",
              checked: () => selectedProfile?.qaText ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaText", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.qaUpdatePush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("qaUpdatePush", event),
            }
          ],
        },
      ],
    },
    {
      name: "Scheduled Plant Events",
      description:
        "Get information related to scheduled plant events such as Weekend Production, Shutodowns, etc.",
      settings: [
        {
          name: "Plant Shutdowns",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.plantShutdown ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("plantShutdown", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.plantShutdownPush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("plantShutdownPush", event),
            }
          ],
        },
        {
          name: "Weekend Production",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.weekendProduction ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("weekendProduction", event),
            },
            {
              name: "Text",
              checked: () => selectedProfile?.wpText ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("wpText", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.weekendProductionPush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("weekendProductionPush", event),
            }
          ],
        },
      ],
    },
    {
      name: "Daily/Weekly Reporting",
      description:
        "Customize the notifications for daily and weekly reporting.",
      settings: [
        {
          name: "Weekly Summaries",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.weeklySummary ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("weeklySummary", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.weeklySummaryPush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("weeklySummaryPush", event),
            }
            
          ],
        },
        {
          name: "Daily Summaries",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.dailySummary ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("dailySummary", event),
            },
            {
              name: "Push Notifications",
              checked: () => selectedProfile?.dailySummaryPush ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("dailySummaryPush", event),
            },
          ],
        },
      ],
    },
    {
      name: "Additional Hours",
      description:
        "Get additional hour request notifications set to your device.",
      settings: [
        {
          name: "Additional Hours",
          options: [
            {
              name: "Email",
              checked: () => selectedProfile?.additionalHours ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("additionalHours", event),
            },
            {
              name: "Text",
              checked: () => selectedProfile?.ahText ?? false,
              onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                settingUpdated("ahText", event),
            },
          ],
        },
      ],
    },
  ];

  useEffect(() => {
    if (saving) {
      setShowPopup(true);
      setOpacityClass("opacity-100");
      const timeoutId = setTimeout(() => {
        setOpacityClass("opacity-0");
        setShowPopup(false);
      }, 2000); // Duration of popup in milliseconds
    }
  }, [saving]);

  useEffect(() => {
    if (!initialToggle) {
      setInitialToggle(true);
      return;
    }

    if (props.enableAllNotifications) {
      enableAll(true);
    } else if (isAllNotificationEnabled()) {
      enableAll(false);
    }
  }, [props.enableAllNotifications]);

  const enableAll = (value: boolean) => {
    const profileClone = JSON.parse(JSON.stringify(selectedProfile));

    profileClone.qaCreation = value;
    profileClone.qaUpdate = value;
    profileClone.qaCreationPush = value;
    profileClone.qaUpdatePush = value;
    profileClone.qaText = value;
    profileClone.plantShutdown = value;
    profileClone.plantShutdownPush = value;
    profileClone.weekendProduction = value;
    profileClone.weekendProductionPush = value;
    profileClone.wpText = value;
    profileClone.weeklySummary = value;
    profileClone.weeklySummaryPush = value;
    profileClone.dailySummary = value;
    profileClone.dailySummaryPush = value;
    profileClone.additionalHours = value;
    profileClone.ahText = value;

    setSelectedProfile(profileClone);

    setTimeout(() => {
      updatePerson(profileClone);
    }, 0);
  };

  const isAllNotificationEnabled = (updatedPerson = selectedProfile) => {
    const notificationArray = [
      updatedPerson.qaCreation,
      updatedPerson.qaUpdate,
      updatedPerson.qaCreationPush,
      updatedPerson.qaUpdatePush,
      updatedPerson.plantShutdown,
      updatedPerson.plantShutdownPush,
      updatedPerson.weekendProduction,
      updatedPerson.weekendProductionPush,
      updatedPerson.weeklySummary,
      updatedPerson.weeklySummaryPush,
      updatedPerson.dailySummary,
      updatedPerson.dailySummaryPush,
      updatedPerson.additionalHours,
      updatedPerson.ahText,
      updatedPerson.wpText,
      updatedPerson.qaText,
    ];

    return !notificationArray.filter(
      (x) => x === false || x == null || (x as any) === 0,
    ).length;
  };

  const checkAllEnabled = (updatedPerson = selectedProfile) => {
    // Flip toggle if all notifications are enabled
    if (updatedPerson) {
      if (isAllNotificationEnabled(updatedPerson)) {
        props.setEnableAllNotifications(true);
      } else {
        props.setEnableAllNotifications(false);
      }
    }
  };

  useEffect(() => {
    const getProfiles = async () => {
      try {
        const [profilesResult, fqmProfilesResult] = await Promise.all([
          dispatch(getProfilesAction()),
          dispatch(getFQMProfiles())
        ]);

        if (profilesResult.type.endsWith('/rejected') || fqmProfilesResult.type.endsWith('/rejected')) {
          throw new Error('Failed to fetch profiles');
        }

        const profiles = profilesResult.payload as Person[];
        const fqmProfiles = fqmProfilesResult.payload as Person[];
        
        setProfiles([...profiles, ...fqmProfiles]);
      } catch (error) {
        console.error('Error fetching profiles:', error);
      }
    };

    setTimeout(() => {
      getProfiles();
    }, 0);
  }, []);

  useEffect(() => {
    if (!props.selectedSupplier) {
      setSelectedProfile(null as any as Person);
      return;
    }

    if (!profiles?.length) {
      setSelectedProfile(null as any as Person);
      return;
    }

    const profile =
      profiles.find((p) => p.supplier === props.selectedSupplier.id) ??
      (null as any as Person);
    setSelectedProfile(profile);
    checkAllEnabled(profile);
    setInitialLoadComplete(true);
    
    // Only fetch OEMs if we're not currently updating them
    if (!isUpdatingOems.current) {
      getOEMOptions(profile);
    }
  }, [props.selectedSupplier, profiles]);

  const getOEMOptions = async (person?: Person) => {
    if (!props.selectedSupplier?.id) {
      return [];
    }
    
    if (!person) {
      person = selectedProfile;
    }

    const result = await dispatch(getOEMs(props.selectedSupplier?.id));
    if (result.type.endsWith('/rejected')) {
      throw new Error('Failed to fetch OEMs');
    }

    const agreements = (result.payload as AgreementReturnModel[]).map((agreementReturnModel: AgreementReturnModel) => agreementReturnModel.agreement);
    const oemOptions = agreements.map((agreement: Agreement) => agreement.location?.oem).filter((oem: OEM) => oem?.name).sort((a: OEM, b: OEM) => a.name.localeCompare(b.name));
    const uniqueOems = oemOptions.filter((oem, index, self) =>
      index === self.findIndex((t) => t.id === oem.id)
    );
    
    // Set the unique OEMs
    setUniqueOems(uniqueOems);

    if ((person?.oemSubscriptions?.length ?? 0) > 0) {
      setSelectedOems(uniqueOems.filter((o: OEM) => person?.oemSubscriptions?.some(s => s.oem === o.id)));
    } else {
      setSelectedOems(uniqueOems);
    }

    return uniqueOems;
  }

  const oemChange = (value: MultiValue<{ value: OEM; label: string }>) => {
    const oems = value.map((v) => v.value);
    setSelectedOems(oems);
    updateOems(oems);
  }

  const updateOems = async (oems: OEM[]) => {
    var oemData;

    if (!props.selectedSupplier?.id) {
      return;
    }
    // Set flag to prevent useEffect from refetching OEMs
    isUpdatingOems.current = true;

    // Filter profiles to only include the selected supplier
    const filteredFQMProfiles = profiles.filter((p) => p.supplier === props.selectedSupplier.id && p.person != null);
    const filteredFQMProfilesParent = profiles.filter((p) => filteredFQMProfiles.some(f => f.person === p.id));
    const filteredProfiles = profiles.filter((p) => p.supplier === props.selectedSupplier.id && p.person == null);
    filteredFQMProfilesParent.forEach((p) => {
      if(!filteredProfiles.some(f => f.id === p.id)) {
        filteredProfiles.push(p);
      }
    })
    // If all oems are selected, remove any existing subscriptions
    if (oems.length === uniqueOems.length) {
      oemData = {
        id: props.selectedSupplier.id,
        contacts: filteredProfiles.map(p => ({
          id: p.id,
          oemSubscriptions: [...(p?.oemSubscriptions ?? []).map(o => ({id: o.id, deleted: true}))]
        }))
      }
    } else {
      oemData = {
        id: props.selectedSupplier.id,
        contacts: filteredProfiles.map(p => {
          const preChangeOemSubscriptions = p?.oemSubscriptions ?? [];
          const oemSubscriptions: IOemSubscription[] = [];
          
          // Add new oems to the oemSubscriptions
          oems.forEach(o => {
            const existingSubscription = p?.oemSubscriptions?.find(s => s.oem === o.id);
            if (!existingSubscription) {
              oemSubscriptions.push({oem: o.id, person: p.id});
            }
          });

          // Remove any oems that are not in the selectedOems array
          p?.oemSubscriptions?.forEach(o => {
            if (!oems.some(s => s.id === o.oem)) {
              oemSubscriptions.push({id: o.id, deleted: true});
            }
          });

          //check here to see if we have removed all oems
          const allOemsRemoved = preChangeOemSubscriptions.every(oemSubscription => {
            return oemSubscriptions.some(subscription => subscription.id === oemSubscription.id);
          })
          //if we have removed all oems, and have not added any, add a null oem
          const hasAddedOems = oemSubscriptions.some(subscription => subscription.oem !== null && subscription.deleted == null);
          if (allOemsRemoved && !hasAddedOems) {
            oemSubscriptions.push({oem: null, person: p.id});
          }
          //if we have added any oems, and have a null oem, remove the null oem
          if (hasAddedOems) {
            //delete all null oems from this list
            p?.oemSubscriptions?.forEach(o => {
              if (o.oem === null) {
                if(o.id) {
                  oemSubscriptions.push({id: o.id, deleted: true});
                }
              }
            })
            //remove all null oems without id from list;
            oemSubscriptions.filter((o) => {
              if (o.oem == null && o.id == null){
                return false;
              }
              return true;
            });
          }
          return {
            id: p.id,
            oemSubscriptions: oemSubscriptions
          }
        })
      }
    }

    try {
      const result = await dispatch(updateSupplierOem(oemData));
      const updatedProfiles = result.payload.contacts.filter((c: any) => filteredProfiles.find(p => p.id === c.id));

      // Update filtered profiles with the new OEM subscriptions
      const profileClone = JSON.parse(JSON.stringify(profiles));
      profileClone.forEach((p: Person) => {
        if (p.supplier === props.selectedSupplier.id) {
          const updatedOems = updatedProfiles.find((c: any) => c.id === p.id)?.oemSubscriptions;
          p.oemSubscriptions = (oems.length === uniqueOems.length) ? [] : updatedOems;
        }
      });
      setProfiles(profileClone);

      if (result.type.endsWith('/rejected')) {
        throw new Error('Failed to update OEMs');
      }
    } finally {
      // Reset flag after update is complete
      isUpdatingOems.current = false;
    }
  }

  const settingUpdated = async (
    field: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const profileClone = JSON.parse(JSON.stringify(selectedProfile));
    profileClone[field] = event.target.checked;
    setSelectedProfile(profileClone);

    toggleSaving(true);
    setTimeout(() => {
      checkAllEnabled(profileClone);
      updatePerson(profileClone);
    }, 0);
  };

  const toggleSaving = (isSaving: boolean) => {
    setSaving(isSaving);
    props.setSaveStatus(isSaving);
  };

  const updatePerson = async (updatedPerson: Person) => {
    try {
      setSaving(true);
      props.setSaveStatus(true);

      const updateResult = updatedPerson.person
        ? await dispatch(updateFQMProfiles(updatedPerson))
        : await dispatch(updateProfiles(updatedPerson));

      if (updateResult.type.endsWith('/rejected')) {
        throw new Error('Failed to update profile');
      }

      // Refresh profiles after update
      const [profilesResult, fqmProfilesResult] = await Promise.all([
        dispatch(getProfilesAction()),
        dispatch(getFQMProfiles())
      ]);

      if (profilesResult.type.endsWith('/rejected') || fqmProfilesResult.type.endsWith('/rejected')) {
        throw new Error('Failed to refresh profiles');
      }

      const profiles = profilesResult.payload as Person[];
      const fqmProfiles = fqmProfilesResult.payload as Person[];
      
      setProfiles([...profiles, ...fqmProfiles]);

      // Update selected profile with the new data
      const updatedProfiles = [...profiles, ...fqmProfiles];
      const newSelectedProfile = updatedProfiles.find(p => p.id === updatedPerson.id);
      if (newSelectedProfile) {
        setSelectedProfile(newSelectedProfile);
      }

      // Check if all notifications are enabled
      if (isAllNotificationEnabled(updatedPerson)) {
        props.setEnableAllNotifications(true);
      } else {
        props.setEnableAllNotifications(false);
      }
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setSaving(false);
      props.setSaveStatus(false);
    }
  };

  return (
    <div className="flex h-[calc(100%-100px)] w-full flex-col overflow-auto">
      {showPopup && (
        <div
          className={`fixed right-4 top-4 z-50 rounded bg-green-500 p-4 text-white ${opacityClass}`}
        >
          Saving changes...
        </div>
      )}

      {initialLoadComplete && 
        <div className="flex w-full flex-col border-b-[1px] border-b-[rgba(166,166,166,0.3)] py-[20px]">
          <div>
            <span className="text-[16px] font-bold text-tqsBlack">OEM Preferences</span>
          </div>
          <div>
            <span className="text-[12px] text-[#8E8E93]">
            Only want notifications for specific OEMs? Subscribe to notifications by OEM(s) of your choosing.
            </span>
          </div>
          <Select<{ value: OEM; label: string }, true>
              isMulti
              name="oemSubscriptions"
              options={uniqueOems.map((oem) => ({
                value: oem,
                label: oem.name,
              }))}
              className="basic-multi-select mt-[8px] h-auto w-[350px] max-w-[350px] text-[black]"
              classNamePrefix="select"
              value={selectedOems.map((oem) => ({
                value: oem,
                label: oem.name,
              }))}
              onChange={(value) => oemChange(value)}
              menuPlacement="auto"
              menuPortalTarget={document.body}
              placeholder="Select OEM"
              styles={{
                control: (provided, state) => ({
                  ...provided,
                  height: "auto", // Allow the container to expand
                  maxHeight: "100px", // Limit the height to 100px
                  overflowY: state.hasValue ? "auto" : "visible", // Enable scrolling after reaching 100px
                }),
                menu: (provided) => ({
                  ...provided,
                  color: 'black',
                  zIndex: 9999, // Ensure the dropdown appears above other elements
                }),
                multiValue: (provided) => ({
                  ...provided,
                  color: 'black',
                  maxWidth: "250px", // (Optional) limit the width of selected values
                }),
              }}
            />
        </div>
      }
      
      {initialLoadComplete &&
        notificationSettings.map((section, index) => (
          <div
            className="flex w-full flex-row border-b-[1px] border-b-[rgba(166,166,166,0.3)] py-[20px]"
            key={index}
          >
            <div className="flex w-[400px] flex-col pr-[80px]">
              <div className="flex flex-row items-center gap-[8px]">
                <span className="mb-[4px] text-[16px] font-bold text-tqsBlack">
                  {section.name}
                </span>

                {(section.name === 'Quality Alerts' || section.name === 'Daily/Weekly Reporting') && <div
                  className="flex h-[24px] px-[12px] items-center justify-center rounded-[6px] border-[1px] border-[#fedd00] bg-[#FCF6BF]">
                  <span className="text-[14px] text-[#fedd00] mb-[2px] font-bold">Recommended</span>
                </div>}
              </div>
              <span className="text-[12px] font-semibold text-tqsBlack">
                {section.description}
              </span>
            </div>

            <div className="flex w-[calc(100%-400px)] flex-col gap-y-[10px]">
              {section.settings.map((setting, index) => (
                <div className="flex w-full flex-col" key={index}>
                  <span className="text-[14px] font-bold text-tqsBlack">
                    {setting.name}
                  </span>

                  <div className="mt-[8px] flex w-[200px] flex-col border-[1px] border-[rgba(212,215,217,1)]">

                    {setting.options.map((itm, index) => {
                      return (<div key={index} className="flex w-[100%] flex-row items-center border-b-[1px] border-b-[rgba(212,215,217,1)] p-[4px] pl-[12px]">
                      <input
                        checked={itm.checked()}
                        onChange={(event) => itm.onChange(event)}
                        disabled={!selectedProfile || saving}
                        className="checkbox max-h-[14px] max-w-[14px] rounded-[2px] border-[#212322] [--chkbg:#fedd00] [--chkfg:black]"
                        type="checkbox"
                      />
                      <span className="ml-[8px] text-[14px] text-[rgba(108,112,117,1)]">
                        {itm.name} {itm.name === 'Text' && <span className='italic'>(Coming Soon)</span>}
                      </span>
                    </div>)
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
    </div>
  );
}
